import { useState } from "react";
import { Divider, Portal, styled, Typography } from "@mui/material";
import { Map } from "leaflet";
import { useTranslation } from "react-i18next";

import { useStudyArea } from "hooks/studyAreaHooks";
import { useNavigate } from "hooks/routerHooks";
import { useMeasureConfigs } from "hooks/measureConfigHooks";
import { useConfigParameters } from "hooks/configParametersHooks";
import { useDebounce } from "hooks/debounceHooks";
import { useFilterOptions, useUrlFilterSelection } from "hooks/measureHooks";

import { MeasureImpact } from "types/types";

import { PaddedPaper } from "components/surfaces/PaddedPaper";
import { RenderStudyArea } from "components/map/layers/RenderStudyArea";
import { FilterMeasureSection } from "./FilterMeasureSection";
import { MeasureSelection } from "./MeasureSelection";
import { RenderMeasureConfigs } from "./addMeasurePage/RenderMeasureConfigs";
import { SelectMeasureFooter } from "components/footer/SelectMeasureFooter";

interface Props {
  uuid: string;
  mapRef: Map;
  footerRef: HTMLDivElement;
}

export function SelectMeasureLoader(props: Props) {
  const { uuid, mapRef, footerRef } = props;
  const { t } = useTranslation();
  const { data: studyArea } = useStudyArea(uuid);
  const { data: measureConfigs } = useMeasureConfigs(uuid);
  const { data: configParameters } = useConfigParameters();

  const filterOptions = useFilterOptions(studyArea?.climateRiskList);
  const { selectedFilter, updateFilterSelectionParam } =
    useUrlFilterSelection(filterOptions);

  const navigate = useNavigate();

  const [search, setSearch] = useState<string>("");
  const debouncedSearch = useDebounce(search, 200).trim();

  function onSelectMeasure(measureImpact: MeasureImpact) {
    navigate(`/${uuid}/measure/${measureImpact.measure.id}`);
  }

  return (
    <StyledPaddedPaper padding={4}>
      <Typography variant="h2" component="h1">
        {t("label.pickAMeasure")}
      </Typography>
      <FilterMeasureSection
        search={search}
        setSearch={setSearch}
        selectedFilter={selectedFilter}
        filterOptions={filterOptions}
        updateFilterSelectionParam={updateFilterSelectionParam}
      />
      <Divider />
      {studyArea && measureConfigs && configParameters && (
        <MeasureSelection
          measureImpacts={studyArea.measureImpactList}
          search={debouncedSearch}
          selectedFilter={selectedFilter}
          onSelectMeasure={onSelectMeasure}
        />
      )}
      {measureConfigs && (
        <RenderMeasureConfigs measureConfigs={measureConfigs} mapRef={mapRef} />
      )}
      {studyArea && (
        <RenderStudyArea
          mapRef={mapRef}
          studyAreaGeo={studyArea.geometry_4326}
        />
      )}
      <Portal container={footerRef}>
        <SelectMeasureFooter uuid={uuid} />
      </Portal>
    </StyledPaddedPaper>
  );
}

const StyledPaddedPaper = styled(PaddedPaper)`
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
  overflow: hidden;
`;
